import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Shirt, Droplets, Flame, Scissors, Footprints, MapPin, Clock, Phone, ArrowRight, Truck, Tag, Sparkles } from "lucide-react";
import { copy } from "@/lib/copy";

// Instagram glyph in its real brand gradient (lucide's icon is monochrome).
function InstagramGlyph({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <defs>
        <radialGradient id="ig-grad" cx="30%" cy="107%" r="150%">
          <stop offset="0%" stopColor="#fdf497" />
          <stop offset="5%" stopColor="#fdf497" />
          <stop offset="45%" stopColor="#fd5949" />
          <stop offset="60%" stopColor="#d6249f" />
          <stop offset="90%" stopColor="#285AEB" />
        </radialGradient>
      </defs>
      <rect x="2" y="2" width="20" height="20" rx="5.5" fill="url(#ig-grad)" />
      <rect x="6" y="6" width="12" height="12" rx="4" fill="none" stroke="#fff" strokeWidth="1.8" />
      <circle cx="12" cy="12" r="3" fill="none" stroke="#fff" strokeWidth="1.8" />
      <circle cx="16.4" cy="7.6" r="1.1" fill="#fff" />
    </svg>
  );
}
import { api, fileUrl } from "@/lib/api";
import { fromPrice, money } from "@/lib/format";
import { activeStatuses } from "@/lib/status";
import { useCustomerProfile } from "@/hooks/useLocal";
import { useShop } from "@/context/ShopContext";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Spinner, ErrorState } from "@/components/common/States";

const CATEGORY_ICON = {
  "Dry Clean": Shirt,
  "Wash n Fold": Droplets,
  Press: Flame,
  Tailoring: Scissors,
};

export default function Home() {
  const navigate = useNavigate();
  const { shop } = useShop();
  const { profile } = useCustomerProfile();
  const phone = profile?.phone ?? "";
  const [heroFailed, setHeroFailed] = useState(false);

  const servicesQuery = useQuery({ queryKey: ["services"], queryFn: api.getServices });
  const bookingsQuery = useQuery({ queryKey: ["bookings", phone], queryFn: () => api.getBookings(phone), enabled: !!phone });

  const activeBooking = bookingsQuery.data?.find((b) => activeStatuses.includes(b.status));
  const popular = (servicesQuery.data ?? []).filter((s) => s.popular).slice(0, 6);
  const heroImg = shop?.home_hero_image_path && !heroFailed ? fileUrl(shop.home_hero_image_path) : null;

  return (
    <div className="animate-fade-up space-y-8">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-3xl">
        <div className="relative grid min-h-[320px] md:grid-cols-2">
          <div className="relative z-10 flex flex-col justify-center gap-4 bg-primary p-8 text-primary-foreground md:p-12">
            <p className="text-sm font-medium uppercase tracking-widest opacity-80">{shop?.tagline ?? "Premium garment care"}</p>
            <h1 className="font-display text-4xl font-extrabold leading-tight sm:text-5xl whitespace-pre-line">
              {shop?.home_hero_title || "Fresh fits,\neffortlessly."}
            </h1>
            <p className="max-w-md text-sm opacity-90 md:text-base">
              {shop?.home_hero_subtitle || "Dry clean · Wash n Fold · Press · Shoes · Tailoring — doorstep pickup across Bhubaneswar"}
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <Link to="/booking" data-testid="hero-book-button" className="rounded-full bg-primary-foreground px-6 py-3 text-sm font-bold text-primary shadow-sm transition hover:opacity-90">
                {copy(shop, "copy_home_book_button", "Book a pickup")}
              </Link>
              <Link to="/games" className="rounded-full border border-primary-foreground/40 px-6 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary-foreground/10">
                Play & earn
              </Link>
            </div>
          </div>
          <div className="relative hidden md:block">
            {heroImg ? (
              <img src={heroImg} alt="Goldys" onError={() => setHeroFailed(true)} className="absolute inset-0 h-full w-full object-cover" />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-[hsl(var(--brand-secondary))]">
                <span className="font-display text-7xl font-black text-primary-foreground/30">G</span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Active order */}
      {activeBooking ? (
        <Link
          to={`/order/${activeBooking.id}`}
          data-testid="home-active-order-card"
          className="block rounded-2xl bg-[hsl(var(--brand-tertiary))] p-5 transition hover:opacity-90"
        >
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold text-primary">Active order</p>
              <p className="mt-0.5 font-display text-base font-semibold">
                {activeBooking.code} · {activeBooking.items.reduce((a, i) => a + i.qty, 0)} items · {money(activeBooking.total)}
              </p>
            </div>
            <StatusBadge status={activeBooking.status} mode={activeBooking.mode} />
          </div>
          <div className="mt-3 flex items-center gap-1 text-sm font-semibold text-primary">
            Track order <ArrowRight className="h-4 w-4" />
          </div>
        </Link>
      ) : null}

      {/* Categories */}
      <section>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold">What do you need?</h2>
          <Link to="/booking" className="text-sm font-semibold text-primary">All prices onwards</Link>
        </div>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {(shop?.categories ?? []).map((c) => {
            const Icon = CATEGORY_ICON[c] || Footprints;
            const count = (servicesQuery.data ?? []).filter((s) => s.category === c && s.unit !== "call").length;
            return (
              <Link
                key={c}
                to={`/booking?category=${encodeURIComponent(c)}`}
                data-testid={`home-category-${c.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}
                className="rounded-2xl bg-[hsl(var(--brand-tertiary))] p-4 transition hover:scale-[1.02]"
              >
                <Icon className="h-6 w-6 text-primary" />
                <p className="mt-3 font-display text-sm font-semibold">{c}</p>
                <p className="text-xs text-muted-foreground">{count} items</p>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Popular */}
      <section>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold">Popular services</h2>
          <Link to="/booking" className="text-sm font-semibold text-primary">Book now</Link>
        </div>
        {servicesQuery.isPending ? (
          <Spinner />
        ) : servicesQuery.isError ? (
          <ErrorState message="Couldn't load the catalog." onRetry={() => servicesQuery.refetch()} />
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {popular.map((s) => (
              <Link key={s.service_id} to="/booking" data-testid={`home-service-${s.service_id}`} className="rounded-2xl bg-card border border-border p-4 transition hover:border-primary/40">
                <p className="font-display text-sm font-semibold line-clamp-2">{s.name}</p>
                <p className="text-xs text-muted-foreground">{s.unit}</p>
                <p className="mt-2 font-display text-base font-bold text-primary">{fromPrice(s.price)}</p>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Why + Visit */}
      <div className="grid gap-6 md:grid-cols-2">
        <section>
          <h2 className="mb-4 font-display text-lg font-bold">Why Goldys</h2>
          <div className="space-y-3 rounded-2xl bg-secondary p-5">
            {[
              { icon: Truck, text: shop?.home_why_bullet_pickup || "Doorstep pickup & delivery across Bhubaneswar" },
              { icon: Clock, text: shop?.turnaround ?? "Standard delivery T+4 days · Express 48h / 36h" },
              { icon: Tag, text: shop?.price_note ?? "All prices are onwards; final price after inspection." },
              { icon: Sparkles, text: shop?.home_why_bullet_fabric_care || "Expert care for silk, wool and delicate fabrics" },
            ].map((row, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[hsl(var(--brand-tertiary))] text-primary">
                  <row.icon className="h-4 w-4" />
                </div>
                <p className="text-sm text-foreground/80">{row.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="mb-4 font-display text-lg font-bold">Visit us</h2>
          <div className="space-y-3 rounded-2xl border border-border p-5">
            <div className="flex items-center gap-3 text-sm">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span>{shop?.address}</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span>{shop?.hours}</span>
            </div>
            {(shop?.phone?.length ? shop.phone : []).map((num, i) => (
              <a key={`${num}-${i}`} href={`tel:${num.replace(/\D/g, "")}`} data-testid={`home-call-shop-${i}`} className="flex items-center gap-3 text-sm font-semibold text-primary underline">
                <Phone className="h-4 w-4" />
                {num}
              </a>
            ))}
            {shop?.address ? (
              <a
                href={`https://maps.google.com/?q=${encodeURIComponent(shop.address)}`}
                target="_blank"
                rel="noreferrer"
                data-testid="home-directions"
                className="inline-flex items-center gap-1 text-sm font-semibold text-primary"
              >
                Get directions <ArrowRight className="h-4 w-4" />
              </a>
            ) : null}
            {shop?.instagram_url ? (
              <a href={shop.instagram_url} target="_blank" rel="noreferrer" data-testid="instagram-link" className="inline-flex items-center gap-2 text-sm font-semibold text-primary">
                <InstagramGlyph className="h-5 w-5" /> Follow us
              </a>
            ) : null}
          </div>
        </section>
      </div>
    </div>
  );
}
